import Axios, { AxiosResponse } from "axios";
import { StringKeyValueMap, getAllCookies, setSameSiteCookie, deleteCookie } from "../utils/webPage";
import { FwlinkHelper } from "../FwlinkHelper";
import { Accounts, LocalServerActions, PlatformActions, UserInfo, UserInfoNoLogo } from "./PlatformActions";
import { generateSwitchUrl } from "../meCtrl";

enum Cookie {
    AccessToken = "AccessToken",
    AccessTokenExpiry = "AccessTokenExpiry",

    /** User vsid associated with the present token. Could be missing if error while getting user */
    AccessUserVsid = "AccessUserVsid",
}

const IdentityMeLogoUrl: string = "https://api.vstsusers.visualstudio.com/_apis/Profile/Profiles/me/Avatar?size=Large";
const TOKEN_TEST_USER: string = "test.post.devcom3";

export class WebActions implements PlatformActions {
    /** used to check if token is usable or needs refresh */
    private static NearExpiryTimeMin: number = 5;
    /** used to setup auto-refresh for token if needed on "very close to expiry" time */
    private static VeryNearExpiryTimeMin: number = 5; // auto-refresh this many minutes before expiry

    private baseUriResolve: Promise<string>;

    public constructor(baseUri: string) {
        this.baseUriResolve = FwlinkHelper.ResolveUrl(baseUri);
        this.loadAccessTokenFromCookieIfAny();
    }

    public get(authRequired: boolean, partialUri: string): Promise<any> {
        return this.send(authRequired, "get", partialUri);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    public post(authRequired: boolean, partialUri: string, data: any): Promise<any> {
        return this.send(authRequired, "post", partialUri, data);
    }

    public async signOutAndIn(): Promise<UserInfo> {
        this.clearAccessTokens();
        window.location.href = generateSwitchUrl(); // TODO: also delete cookies from the server in case a user manually goes to /signin
        return null; // page will refresh before this call completes
    }

    public async signOut(): Promise<void> {
        this.clearAccessTokens();
        window.location.href = "/signout"; // TODO: also delete cookies from the server in case a user manually goes to /signout
    }

    public async checkForSignIn(): Promise<Accounts | UserInfo | null> {
        const authenticated: boolean = await this.refreshTokenAndLoadUserBasicInfoIfNeeded();
        if (!authenticated) {
            return null;
        }

        if (this.user.logo == null) { // if no logo loaded yet
            try {
                const logo: AxiosResponse<UserLogo> = await this.sendFullUrl(true, "get", IdentityMeLogoUrl);
                this.user.logo = "data:image/png;base64," + logo.data.value;
            }
            catch (err) {
                if (err.code === "ERR_NETWORK" &&
                    this.accessToken === TOKEN_TEST_USER) {
                    const SMALL_SQUARE_AND_CIRCLE_LOGO: string = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNmZmYiLz48Y2lyY2xlIGN4PSI1MCIgY3k9IjUwIiByPSIzMCIgZmlsbD0iI2NjYyIvPjwvc3ZnPg==";
                    this.user.logo = SMALL_SQUARE_AND_CIRCLE_LOGO;
                    return this.user;
                }

                if (err.response.status === 401) { // if server lost auth session (valid auth token and lost auth session can happen on server restart)
                    return this.user; // ignore error for now, since it's just user's logo
                }

                throw err;
            }
        }

        return this.user;
    }

    public getNativeActions(): null {
        return null;
    }

    public getLocalServerActions(): LocalServerActions {
        return {
            get: (authRequired, partialUri) => this.sendFullUrl(authRequired, "get", partialUri),
            post: (authRequired, partialUri, data) => this.sendFullUrl(authRequired, "post", partialUri, data),
        };
    }

    private async sendFullUrl(authRequired: boolean, method: "get" | "post", url: string, data?: any): Promise<any> {
        const headers = await this.getHeaders(authRequired);
        const response: AxiosResponse<any> = await Axios.request({
            method,
            url,
            headers,
            data,
        });

        return response;
    }

    private async send(authRequired: boolean, method: "get" | "post", partialUri: string, data?: any): Promise<any> {
        const [baseUri, headers] = await Promise.all([this.baseUriResolve, this.getHeaders(authRequired)]);
        const response: AxiosResponse<any> = await Axios.request({
            method,
            url: `${baseUri}${partialUri}`,
            headers,
            data,
        });

        return response;
    }

    private async getHeaders(authRequired: boolean): Promise<any> {
        if (authRequired) {
            const authenticated: boolean = await this.refreshTokenAndLoadUserBasicInfoIfNeeded();
            if (!authenticated) {
                return Promise.reject("Please sign in");
            }
        }

        if (!WebActions.isTokenUsable(this.accessToken, this.accessTokenExpiry)) {
            return {
                'Content-Type': 'application/json',
            };
        } else {
            return {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + this.accessToken,
            };
        }
    }

    private accessToken: string | null;
    private accessTokenExpiry: Date | null;

    /** User vsid from cookies. 
     * Used to compare with server VSID to check if access token belong to the same user as the server 
     */
    private accessUserVsid: string | null;

    /** User info from server */
    private user: UserInfo | null;
    private refreshTokenPromise: Promise<boolean> | null;

    private async refreshTokenAndLoadUserBasicInfoIfNeeded(): Promise<boolean> {
        try {
            if (this.refreshTokenPromise) { // if already working on refreshing token
                return this.refreshTokenPromise;
            }

            this.refreshTokenPromise = this.refreshTokenAndLoadUserBasicInfoIfNeededNoPromiseCache();
            const result: boolean = await this.refreshTokenPromise;
            this.refreshTokenPromise = null;

            return result;
        }
        catch (err) {
            this.refreshTokenPromise = null;
            throw err;
        }
    }

    /** Calls refresh for token when near-expiry. Returns true if auto-refresh setup; false if token is unusable (very near expiry) */
    private setupAutoRefresh(accessTokenExpiry: Date): boolean {
        const ONE_MINUTE: number = 1000 * 60;
        const HALF_MINUTE: number = ONE_MINUTE / 2;
        const nowMsec: number = new Date().getTime();
        const refreshAfterMsec: number = accessTokenExpiry.getTime() - nowMsec - ONE_MINUTE * WebActions.VeryNearExpiryTimeMin;
        if (refreshAfterMsec < HALF_MINUTE) {
            return false;
        }

        setTimeout(() => {
            this.refreshTokenAndLoadUserBasicInfoIfNeeded();
        }, refreshAfterMsec);

        return true;
    }

    /** returns true if token refreshed, false if sign-in required */
    private async refreshTokenAndLoadUserBasicInfoIfNeededNoPromiseCache(): Promise<boolean> {
        // if token is usable (not expired) and we know which user it belongs to
        if (WebActions.isTokenUsable(this.accessToken, this.accessTokenExpiry) && this.accessUserVsid != null) {
            // fast check - is everything matching?
            if (this.accessUserVsid != null && this.user != null && this.accessUserVsid === this.user.vsid) { // if server and cookie user ID match
                // ... fast match
                return true;
            }

            // if no user from server loaded yet, load it
            if (this.user == null) { // if no user loaded yet
                try {
                    // Note: logo is not returned from server to reduce server load
                    const res: AxiosResponse<UserInfoNoLogo> = await Axios.get("/user");
                    this.user = res.data;
                }
                catch (err) {
                    // valid auth token and lost auth session can happen on server restart, check for it
                    if (err.response.status !== 401) { // if server did not lose auth session, then it's a different error
                        throw err;
                    }
                }
            }

            // Check if server user and current user from cookies match. 
            // Notes:
            // 1. Can mismatch during account switch if cookies are not cleared. 
            // 2. Cookies may not be cleared if we don't pass through DevCom's backend /signin-visualstudio that clears them
            if (this.accessUserVsid != null && this.user != null && this.accessUserVsid === this.user.vsid) { // if server and cookie user ID match
                WebActions.storeTokenInCookie(this.accessToken, this.accessTokenExpiry, this.accessUserVsid);

                // since we just got the /user from server, setup auto-refresh for the token
                if (this.setupAutoRefresh(this.accessTokenExpiry)) {
                    // ... slower match, still matching
                    return true;
                }
            }

            // console.log(`DEBUG: USER MISMATCH: Server: ${this.user.vsid}, Cached cookie: ${this.accessUserVsid}`); // uncomment for debugging
            this.clearAccessTokens();
        }

        try {
            const res: AxiosResponse<TokenInfo> = await Axios.get("/token?refresh=true");

            if (!WebActions.isTokenUsable(res.data.access_token, res.data.expiry)) {
                this.clearAccessTokens();
                throw new Error("Sign in failed: cannot get access token");
            }

            this.user = res.data.user;
            this.accessToken = res.data.access_token;
            this.accessTokenExpiry = new Date(Date.parse(res.data.expiry));
            // nikolami: 2022-11-24: DEBUG: uncomment below line and comment first large if() block in this function to debug near-expiry tokens
            // that cause user returned but not signed in correctly
            // this.accessTokenExpiry = new Date();
            this.accessUserVsid = this.user.vsid;
            WebActions.storeTokenInCookie(this.accessToken, this.accessTokenExpiry, this.accessUserVsid);

            // setup auto-refresh if needed on very-near time to expiry
            if (!this.setupAutoRefresh(this.accessTokenExpiry)) {
                this.clearAccessTokens();
                throw new Error("Sign in failed: refresh token has very short validity period");
            }

            return true;
        }
        catch (error) {
            this.clearAccessTokens();
            console.error(error);
            return false;
        }
    }

    private clearAccessTokens(): void {
        this.accessToken = null;
        this.accessTokenExpiry = null;
        this.accessUserVsid = null;

        WebActions.storeTokenInCookie(this.accessToken, this.accessTokenExpiry, this.accessUserVsid);
    }

    private loadAccessTokenFromCookieIfAny(): void {
        const cookies: StringKeyValueMap = getAllCookies();
        this.accessToken = cookies[Cookie.AccessToken];
        this.accessUserVsid = cookies[Cookie.AccessUserVsid];
        const expiry: string = cookies[Cookie.AccessTokenExpiry];

        if (WebActions.isTokenUsable(this.accessToken, expiry)) {
            this.accessTokenExpiry = new Date(Date.parse(expiry));
            return;
        }

        this.accessToken = null;
        this.accessUserVsid = null;
        this.accessTokenExpiry = null;
    }

    private static storeTokenInCookie(accessToken: string | null, accessTokenExpiry: Date | null, accessUserVsid: string | null): void {
        if ((accessToken == null) || (accessTokenExpiry == null)) {
            deleteCookie(Cookie.AccessToken);
            deleteCookie(Cookie.AccessTokenExpiry);
        }
        else {
            setSameSiteCookie(Cookie.AccessToken, accessToken);
            setSameSiteCookie(Cookie.AccessTokenExpiry, accessTokenExpiry.toISOString());
        }

        if (accessUserVsid == null) {
            deleteCookie(Cookie.AccessUserVsid);
        }
        else {
            setSameSiteCookie(Cookie.AccessUserVsid, accessUserVsid);
        }
    }

    private static isTokenUsable(token: string | null, expiry: IsoDateString | Date | null, expiryThresholdMinutes: number = WebActions.NearExpiryTimeMin): boolean {
        if ((token == null) || (expiry == null)) {
            return false;
        }

        const expiryMsec: number = (expiry instanceof Date) ? expiry.getTime() : Date.parse(expiry);
        if (Number.isNaN(expiryMsec)) { // if not valid date
            return false;
        }

        const nowMsec: number = new Date().getTime();
        const ONE_MINUTE: number = 1000 * 60;
        const expiryThresholdMsec: number = ONE_MINUTE * expiryThresholdMinutes;
        if ((expiryMsec - nowMsec) < expiryThresholdMsec) { // if near expire
            return false;
        }

        return true;
    }
}

type IsoDateString = string;

interface TokenInfo {
    access_token: string; // "eyJ0eXAi..."
    expiry: IsoDateString; // "2020-02-25T04:55:54.4503979Z"
    user: UserInfo; // used to avoid calling /user API unnecessarily
}

/** returned from https://api.vstsusers.visualstudio.com/_apis/Profile/Profiles/me/Avatar?size=Medium */
interface UserLogo {
    value: string; // base64 encoded png: "iVBORw0KGgoAAAANSUhE..."
    timestamp: string; // "2018-06-19T12:26:40.8465503+00:00"
    size: string; // "medium" (large or small are options too)
    isAutoGenerated: boolean; // e.g. false
}
